import React, { useState, useMemo } from 'react';
import { FlatList, StyleSheet, Animated, View, Text } from 'react-native';
import PropTypes from 'prop-types';
import Utility from '../../utils/Utility';
import colors from '../../theme/Colors';
import OfferCartItemExpanded from './OfferCartItemExpanded';
import { SCREEN_CONSTANTS } from '../../config/ScreenConstants';

const keyExtractor = (item, index) => item?.sku_id?.toString() || index.toString();

const ListCartItems = (props) => {
  const {
    cartItems = [],
    navigation,
    hideAddToCart = false,
    itemData = {},
    scrollY,
    scrollToList = () => {},
    setCurrentList = () => {},
    refreshing = false,
    force_apply_coupon = '',
    list_to_sku_id = [],
  } = props;

  if (refreshing) {
    return null;
  }

  const flatListRef = React.useRef();
  const [currentIndex, setCurrentIndex] = useState(0);

  const comboColors = [
    '#2397F1', '#C26F18', '#01B460', '#EE9B0F',
    '#2397F1', '#C26F18', '#01B460', '#EE9B0F',
  ];

  const cartItemsToRender = useMemo(() => {
    return Object.entries('4').reduce((filteredCartItems, [listId]) => {
      const bgColor = comboColors.shift(); // Assign a background color for the list

      // Filter cart items based on SKU IDs belonging to the list
      const matchingCartItems = cartItems?.filter((cartItem) => {
        return list_to_sku_id?.includes?.(cartItem?.sku_id);
      }) || [];

      // Add the filtered cart items to the final list
      filteredCartItems.push(...matchingCartItems);

      // Add placeholders if less than 4 items are present
      const totalQuantityForThisList = matchingCartItems.length;

      if (totalQuantityForThisList < 4) {
        const emptyCardArray = Array(4 - totalQuantityForThisList).fill({
          type: 'empty',
          list_id: listId,
          bgColor,
        });

        filteredCartItems.push(...emptyCardArray);
      }

      return filteredCartItems;
    }, []);
  }, [cartItems, list_to_sku_id, comboColors]);

  let cartItemsStyle = {};
  let listTitleStyle = {};
  const widthRatio = 0.7;

  if (Utility.isPresent(scrollY)) {
    cartItemsStyle = {
      marginRight: 8,
      zIndex: 1000,
      height: scrollY.interpolate({
        inputRange: [0, 100, 200, 300, 400],
        outputRange: [202, 180, 160, 140, 122],
        extrapolate: 'clamp',
      }),
      width: scrollY.interpolate({
        inputRange: [0, 100, 200, 300, 400, 500],
        outputRange: [
          202 * widthRatio,
          180 * widthRatio,
          160 * widthRatio,
          140 * widthRatio,
          122 * widthRatio,
          122 * widthRatio,
        ],
      }),
    };

    listTitleStyle = {
      height: scrollY.interpolate({
        inputRange: [0, 100, 200, 300, 400],
        outputRange: [30, 20, 10, 0, 0],
        extrapolateLeft: 'clamp',
      }),
      width: scrollY.interpolate({
        inputRange: [0, 100, 200, 300, 400, 500],
        outputRange: [
          202 * widthRatio,
          180 * widthRatio,
          160 * widthRatio,
          140 * widthRatio,
          122 * widthRatio,
          122 * widthRatio,
        ],
        extrapolateLeft: 'clamp',
      }),
    };
  }

  const renderItem = ({ item, index }) => {
    if (!item || item.quantity <= 0) {
      return null;
    }
    const comboOfferCardPrompt = Utility.isPresent(item.comboOfferCardPrompt) ? item.comboOfferCardPrompt : '';

    return (
      <View style={styles.itemContainer}>
        <Animated.View style={listTitleStyle}>
          <Animated.Text style={styles.listTitle} numberOfLines={1}>
            {comboOfferCardPrompt}
          </Animated.Text>
        </Animated.View>
        <Animated.View style={cartItemsStyle}>
          <OfferCartItemExpanded
            cartItem={item}
            navigation={navigation}
            hideAddToCart={hideAddToCart}
            itemData={itemData}
            setCurrentIndex={() => {
              setCurrentIndex(index);
              scrollToList();
            }}
            currentIndex={currentIndex}
            index={index}
            setCurrentList={setCurrentList}
            scrollY={scrollY}
            force_apply_coupon={force_apply_coupon}
            comboOfferCardPrompt={comboOfferCardPrompt}
            previousScreen={SCREEN_CONSTANTS.MORE_ITEMS}
          />
        </Animated.View>
      </View>
    );
  };

  return (
    <FlatList
      ref={flatListRef}
      data={cartItemsToRender}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      style={styles.container}
      extraData={cartItems}
      horizontal
      contentContainerStyle={styles.largeCartItemsContainer}
      showsHorizontalScrollIndicator={false}
    />
  );
};

ListCartItems.propTypes = {
  cartItems: PropTypes.array,
  navigation: PropTypes.object.isRequired,
  hideAddToCart: PropTypes.bool,
  itemData: PropTypes.object,
  scrollY: PropTypes.object,
  scrollToList: PropTypes.func,
  setCurrentList: PropTypes.func,
  refreshing: PropTypes.bool,
  force_apply_coupon: PropTypes.string,
  list_to_sku_id: PropTypes.arrayOf(PropTypes.string),
};

const styles = StyleSheet.create({
  container: { backgroundColor: colors.white },
  itemContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  largeCartItemsContainer: {
    paddingLeft: 12,
    paddingTop: 16,
    paddingBottom: 16,
  },
  listTitle: {
    marginTop: 6,
    height: '54%',
    fontSize: 12,
    fontFamily: 'Roboto-Medium',
    color: colors.black,
  },
});

export default ListCartItems;
